import { cn } from '@utils/cn';

const PREVIEW_GAP_REM = 0.75; // gap-3

const previewAxisMaxSize = (cells: number): string => {
  if (cells <= 1) return '100%';
  const gaps = (cells - 1) * PREVIEW_GAP_REM;
  return `calc((100% - ${gaps}rem) / ${cells})`;
};

/**
 * Max box for each artwork inside the fixed-aspect preview.
 * Near-square grid (ceil(sqrt(n)) cols) keeps each image as large as possible.
 */
const previewImageMaxSize = (
  count: number,
): { maxHeight: string; maxWidth: string } => {
  const cols = Math.max(1, Math.ceil(Math.sqrt(count)));
  const rows = Math.max(1, Math.ceil(count / cols));

  return {
    maxHeight: previewAxisMaxSize(rows),
    maxWidth: previewAxisMaxSize(cols),
  };
};

type OrderReviewPreviewProps = {
  images: {
    id: string;
    src: string;
    alt: string;
  }[];
  aspectClassName?: string;
  className?: string;
};

export const OrderReviewPreview = ({
  aspectClassName = 'aspect-square',
  className,
  images,
}: OrderReviewPreviewProps) => {
  if (images.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        aspectClassName,
        'w-full overflow-hidden rounded-sm bg-[#F3F3F3] p-3',
        className,
      )}
    >
      <div className="flex h-full w-full flex-wrap content-center items-center justify-center gap-3">
        {images.map((image) => {
          const maxSize = previewImageMaxSize(images.length);
          return (
            // Native img keeps artwork AR; Image defaults to object-fit cover.
            <img
              key={image.id}
              alt={image.alt}
              className="object-contain"
              src={image.src}
              style={maxSize}
            />
          );
        })}
      </div>
    </div>
  );
};
